// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { createElement, Component, disableDocumentScroll, enableDocumentScroll } from '../../utils';
import { safeInvoke } from '../utils';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CLS_PREFIX, Z_INDEX_OVERLAY } from '../../constants';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import Backdrop from './Backdrop';
import Layer from '../Layer';

import '../../helpers/base.less';
import './Overlay.less';


/**
 * `Overlay` component.
 */
class Overlay extends Component {
    state = {
        isOpened: false,
    };

    componentDidMount() {
        if (this.props.isOpen) {
            this.overlayWillOpen();
        }
    }

    componentDidUpdate(prevProps) {
        if (!this.props.isOpen && prevProps.isOpen) {
            this.overlayWillClose();
        } else if (this.props.isOpen && !prevProps.isOpen) {
            this.overlayWillOpen();
        }
    }

    componentWillUnmount() {
        if (this.props.isOpen) {
            this.overlayWillClose();
        }
    }

    containerElement = null;

    prevFocusElement = null;

    backdropElement = null;

    overlayWillOpen() {
        disableDocumentScroll();
        this.setState({ isOpened: true }, () => {
            this.prevFocusElement = document.activeElement;
            this.focus();
        });
    }

    overlayWillClose() {
        enableDocumentScroll();

        this.setState({ isOpened: false }, () => {
            if (this.prevFocusElement) {
                this.prevFocusElement.focus();
                this.prevFocusElement = null;
            }
        });
    }

    handleBackdropClick = () => {
        const { onClose, canCloseOnBackdropClick } = this.props;

        if (canCloseOnBackdropClick) {
            safeInvoke(onClose);
        } else {
            this.focus();
        }
    };

    handleContentKeyUp = e => {
        const { onClose } = this.props;

        if (!e.isDefaultPrevented() && e.key === 'Escape') {
            safeInvoke(onClose);
            e.preventDefault();
        }
    };

    focus() {
        if (this.containerElement === null || document.activeElement === null || !this.props.isOpen) {
            return;
        }

        const isFocusOutside = !this.containerElement.contains(document.activeElement);
        if (isFocusOutside) {
            const autofocusElement = this.containerElement.querySelector('[autofocus]');
            const wrapperElement = this.containerElement.querySelector('[tabindex]');
            if (autofocusElement !== null) {
                autofocusElement.focus();
            } else if (wrapperElement !== null) {
                wrapperElement.focus();
            }
        }
    }

    renderContent({ baseClassName, children, canCloseOnEscapePress }) {
        return (
            <CSSTransition
                classNames={`${baseClassName}-`}
                timeout={300}
            >
                <div
                    className={`${baseClassName}__content`}
                    tabIndex={0}
                    onKeyUp={canCloseOnEscapePress ? this.handleContentKeyUp : null}
                >
                    {children}
                </div>
            </CSSTransition>
        );
    }

    renderBackdrop({ baseClassName }) {
        return (
            <CSSTransition
                classNames={`${baseClassName}-`}
                timeout={200}
            >
                <Backdrop
                    innerRef={ref => {
                        this.backdropElement = ref;
                    }}
                    onClick={() => this.handleBackdropClick()}
                />
            </CSSTransition>
        );
    }

    render() {
        const {
            baseClassName,
            className,
            children,
            isOpen,
            placement,
            size,
            onClose,
            canCloseOnBackdropClick,
            canCloseOnEscapePress,
            ...props
        } = this.props;

        if (!isOpen) {
            return null;
        }

        const { isOpened } = this.state;

        return (
            <Layer
                innerRef={ref => (this.containerElement = ref)}
                level={Z_INDEX_OVERLAY}
            >
                <TransitionGroup
                    className={classNames(
                        baseClassName,
                        `${baseClassName}--${placement}`,
                        `${baseClassName}--${size}`,
                        className
                    )}
                    role="dialog"
                    aria-modal="true"
                    {...props}
                >
                    {isOpened ? this.renderBackdrop({ baseClassName }) : null}
                    {isOpened ? this.renderContent({ baseClassName, children, canCloseOnEscapePress }) : null}
                </TransitionGroup>
            </Layer>
        );
    }
}

Overlay.propTypes = {
    /**
     * Toggles visibility.
     */
    isOpen: PropTypes.bool,
    /**
     * Overlay placement.
     */
    placement: PropTypes.oneOf(['center', 'left', 'right']),
    /**
     * Overlay size.
     */
    size: PropTypes.oneOf(['sm', 'md', 'lg']),
    /**
     * onClose handler.
     */
    onClose: PropTypes.func,
    /**
     * Indicating if clicking the backdrop should close the overlay.
     */
    canCloseOnBackdropClick: PropTypes.bool,
    /**
     * Indicating if pressing the escape key should close the overlay.
     */
    canCloseOnEscapePress: PropTypes.bool,
    /**
     * Content of the `Overlay`.
     */
    children: PropTypes.node,
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * @ignore
     */
    baseClassName: PropTypes.string,
};

Overlay.defaultProps = {
    isOpen: false,
    placement: 'center',
    size: 'md',
    onClose: null,
    canCloseOnBackdropClick: true,
    canCloseOnEscapePress: true,
    children: null,
    className: null,
    baseClassName: `${CLS_PREFIX}overlay`,
};

export default Overlay;



// WEBPACK FOOTER //
// ./components/Overlay/Overlay.js